import { useState } from "react";
import BackButton from "../components/BackButton";
import { createJob } from "../models/models";
import { jobTypeOptions } from "../utils/constants";
import { hasCapability } from "../utils/capabilities";
import EquipmentMultiSelect from "../components/EquipmentMultiSelect";

const STATUS_LABEL = {
  requested: "Requested",
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In Progress",
  closed: "Completed",
  canceled: "Canceled",
};

const STATUS_COLOR = {
  requested: { bg: "#fef3c7", fg: "#92400e" },
  unassigned: { bg: "#f3f4f6", fg: "#444" },
  assigned: { bg: "#e8f0fe", fg: "#1a5fb4" },
  in_progress: { bg: "#fff3cd", fg: "#9a6b00" },
  closed: { bg: "#c9efc9", fg: "#1a7f37" },
  canceled: { bg: "#f8d7da", fg: "#c12c2c" },
};

// Mobile JobsPage. Tech-first layout (Mine / Open / All tabs) but with
// admin/manager parity for create-job. Status section + assign-techs UI
// live on JobDetailPageMobile.
export default function JobsPageMobile({
  customers,
  jobsRaw,
  user,
  setSelectedJob,
  onBack,
  actions,
  role,
  capabilities,
}) {
  const canCreateJobs = hasCapability(role, capabilities, "createJobs");

  const [tab, setTab] = useState("mine");
  const [search, setSearch] = useState("");

  // Create-job form state (mobile parity with JobsPage). Hidden behind a
  // "+ New Job" button so the tech-default view stays uncluttered.
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [jobType, setJobType] = useState("");
  const [quotedAmount, setQuotedAmount] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [creating, setCreating] = useState(false);

  const locationsForCustomer =
    customers.find((c) => c.id === customerId)?.locations || [];
  const equipmentForLocation =
    locationsForCustomer.find((l) => l.id === locationId)?.equipment || [];

  const resetCreateForm = () => {
    setTitle("");
    setDescription("");
    setJobType("");
    setQuotedAmount("");
    setCustomerId("");
    setLocationId("");
    setEquipmentIds([]);
  };

  const toggleEquipment = (eqId) =>
    setEquipmentIds((prev) =>
      prev.includes(eqId) ? prev.filter((id) => id !== eqId) : [...prev, eqId]
    );

  const handleCreate = async () => {
    if (!title || !customerId || !locationId) return;
    const isQuoted = jobType === "Quoted";
    const quotedNum = parseFloat(quotedAmount);
    if (isQuoted && (!Number.isFinite(quotedNum) || quotedNum <= 0)) {
      alert("Enter the quoted price (greater than 0) for this job.");
      return;
    }
    setCreating(true);
    try {
      const baseJob = createJob(title);
      const job = {
        ...baseJob,
        description,
        jobType,
        customerId,
        locationId,
        equipmentIds,
        updateUponDiagnostic: false,
        createdBy: user?.email || "unknown",
        ...(isQuoted && {
          costing: {
            ...(baseJob.costing || {}),
            labor: [],
            materials: [],
            expenses: [],
            fuel: [],
            taxes: [],
            billDescription: description || "",
            priceMode: "quoted",
            quotedAmount: quotedNum,
          },
        }),
      };
      await actions.addJob(job);
      resetCreateForm();
      setShowCreate(false);
    } finally {
      setCreating(false);
    }
  };

  const customerName = (id) =>
    customers.find((c) => c.id === id)?.name || "—";
  const locationName = (cId, lId) => {
    const c = customers.find((x) => x.id === cId);
    return c?.locations.find((l) => l.id === lId)?.name || "—";
  };

  const matchesSearch = (job) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const cust = customers.find((c) => c.id === job.customerId);
    const loc = cust?.locations.find((l) => l.id === job.locationId);
    const equipment = (loc?.equipment || []).filter((eq) =>
      (job.equipmentIds || []).includes(eq.id)
    );
    const haystack = [
      job.title,
      job.description,
      job.jobType,
      job.jobNumber,
      cust?.name,
      loc?.name,
      ...equipment.flatMap((eq) => [
        eq.type,
        eq.subType,
        eq.manufacturer,
        eq.modelNumber,
        eq.serialNumber,
        eq.partsList,
      ]),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  };

  const isClosed = (j) => j.status === "closed" || j.status === "canceled";

  // Jobs with a finalized bill are archived; never show on the live board.
  const liveJobs = jobsRaw.filter((j) => !j.billFinalized);

  const filtered = liveJobs
    .filter((j) => {
      if (tab === "mine") {
        return (
          (j.assignedTechs || []).includes(user?.uid) && !isClosed(j)
        );
      }
      if (tab === "open") return !isClosed(j);
      return true; // all
    })
    .filter(matchesSearch)
    .sort(
      (a, b) =>
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );

  const mineCount = liveJobs.filter(
    (j) => (j.assignedTechs || []).includes(user?.uid) && !isClosed(j)
  ).length;
  const openCount = liveJobs.filter((j) => !isClosed(j)).length;
  const allCount = liveJobs.length;

  const tabBtn = (key, label, count) => {
    const active = tab === key;
    return (
      <button
        key={key}
        onClick={() => setTab(key)}
        style={{
          flex: 1,
          padding: "10px 8px",
          fontSize: "14px",
          fontWeight: active ? 600 : 500,
          background: active ? "#1a5fb4" : "white",
          color: active ? "white" : "#444",
          border: "1px solid #d1d5db",
          borderRight: "none",
          cursor: "pointer",
        }}
      >
        {label} <span style={{ opacity: 0.8 }}>({count})</span>
      </button>
    );
  };

  return (
    <div style={{ padding: "8px 12px", fontSize: "15px" }}>
      <BackButton onClick={onBack} />

      <h2 style={{ margin: "0 0 12px", fontSize: "22px" }}>Jobs</h2>

      {canCreateJobs && (
        <div style={{ marginBottom: "12px" }}>
          <button
            onClick={() => setShowCreate((s) => !s)}
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "15px",
              fontWeight: 600,
              background: showCreate ? "#e5e7eb" : "#1a5fb4",
              color: showCreate ? "#444" : "white",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {showCreate ? "Cancel" : "+ New Job"}
          </button>

          {showCreate && (
            <div
              style={{
                marginTop: "10px",
                padding: "12px",
                background: "white",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
              }}
            >
              <label style={{ fontSize: "13px", fontWeight: 600 }}>Title</label>
              <input
                type="text"
                placeholder="Job title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  marginTop: "4px",
                  marginBottom: "8px",
                  boxSizing: "border-box",
                }}
              />

              <label style={{ fontSize: "13px", fontWeight: 600 }}>Description</label>
              <textarea
                placeholder="What needs doing?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  marginTop: "4px",
                  marginBottom: "8px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                }}
              />

              <label style={{ fontSize: "13px", fontWeight: 600 }}>Job Type</label>
              <select
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  marginTop: "4px",
                  marginBottom: "8px",
                  boxSizing: "border-box",
                }}
              >
                <option value="">Select type</option>
                {jobTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>

              {jobType === "Quoted" && (
                <div
                  style={{
                    padding: "10px",
                    background: "#fff7e6",
                    border: "1px solid #f0c674",
                    borderRadius: "4px",
                    marginBottom: "8px",
                  }}
                >
                  <label style={{ fontSize: "13px", fontWeight: 600 }}>
                    Quoted price (customer-locked)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={quotedAmount}
                    onChange={(e) => setQuotedAmount(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      fontSize: "14px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      marginTop: "4px",
                      boxSizing: "border-box",
                    }}
                  />
                  <div style={{ fontSize: "12px", color: "#5a4000", marginTop: "4px" }}>
                    For jobs quoted outside the app. The bill stays locked at
                    this price; tickets/POs still feed costs for P&amp;L.
                  </div>
                </div>
              )}

              <label style={{ fontSize: "13px", fontWeight: 600 }}>Customer</label>
              <select
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setLocationId("");
                  setEquipmentIds([]);
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  marginTop: "4px",
                  marginBottom: "8px",
                  boxSizing: "border-box",
                }}
              >
                <option value="">Select customer</option>
                {[...customers]
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>

              {customerId && (
                <>
                  <label style={{ fontSize: "13px", fontWeight: 600 }}>Location</label>
                  <select
                    value={locationId}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      setEquipmentIds([]);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      fontSize: "14px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      marginTop: "4px",
                      marginBottom: "8px",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Select location</option>
                    {locationsForCustomer.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {locationId && equipmentForLocation.length > 0 && (
                <div style={{ marginBottom: "10px" }}>
                  <label
                    style={{ fontSize: "13px", fontWeight: 600, display: "block", marginBottom: "4px" }}
                  >
                    Equipment (optional)
                  </label>
                  <EquipmentMultiSelect
                    equipment={equipmentForLocation}
                    selectedIds={equipmentIds}
                    onChange={setEquipmentIds}
                  />
                </div>
              )}

              <button
                onClick={handleCreate}
                disabled={creating || !title || !customerId || !locationId}
                style={{
                  width: "100%",
                  padding: "12px",
                  fontSize: "15px",
                  fontWeight: 600,
                  background: "#1a7f37",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  opacity: creating || !title || !customerId || !locationId ? 0.5 : 1,
                }}
              >
                {creating ? "Creating…" : "Create Job"}
              </button>
            </div>
          )}
        </div>
      )}

      <input
        type="text"
        placeholder="Search jobs, customer, equipment…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "15px",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          marginBottom: "12px",
          boxSizing: "border-box",
        }}
      />

      <div
        style={{
          display: "flex",
          marginBottom: "12px",
          borderRadius: "6px",
          overflow: "hidden",
          border: "1px solid #d1d5db",
        }}
      >
        {tabBtn("mine", "Mine", mineCount)}
        {tabBtn("open", "Open", openCount)}
        <button
          onClick={() => setTab("all")}
          style={{
            flex: 1,
            padding: "10px 8px",
            fontSize: "14px",
            fontWeight: tab === "all" ? 600 : 500,
            background: tab === "all" ? "#1a5fb4" : "white",
            color: tab === "all" ? "white" : "#444",
            border: "none",
            cursor: "pointer",
          }}
        >
          All <span style={{ opacity: 0.8 }}>({allCount})</span>
        </button>
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            padding: "24px",
            textAlign: "center",
            color: "#888",
            border: "1px dashed #d1d5db",
            borderRadius: "8px",
          }}
        >
          {tab === "mine"
            ? "Nothing assigned to you."
            : search
            ? "No jobs match your search."
            : "No jobs."}
        </div>
      ) : (
        filtered.map((job) => {
          const sc =
            STATUS_COLOR[job.status] || STATUS_COLOR.unassigned;
          return (
            <button
              key={job.id}
              onClick={() => setSelectedJob(job)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "14px",
                marginBottom: "10px",
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#222",
                    lineHeight: 1.3,
                  }}
                >
                  {job.jobNumber ? `${job.jobNumber} — ` : ""}
                  {job.title}
                </div>
                <span
                  style={{
                    flexShrink: 0,
                    padding: "3px 8px",
                    borderRadius: "999px",
                    background: sc.bg,
                    color: sc.fg,
                    fontSize: "11px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {STATUS_LABEL[job.status] || job.status}
                </span>
              </div>
              <div style={{ fontSize: "13px", color: "#555" }}>
                {customerName(job.customerId)}
              </div>
              <div style={{ fontSize: "13px", color: "#777" }}>
                📍 {locationName(job.customerId, job.locationId)}
              </div>
              {job.jobType && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#888",
                    marginTop: "4px",
                  }}
                >
                  {job.jobType}
                </div>
              )}
            </button>
          );
        })
      )}
    </div>
  );
}
